import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ArrowLeftLineIcon from '@rsuite/icons/ArrowLeftLine'
import PagePreviousIcon from '@rsuite/icons/PagePrevious'
import PageNextIcon from '@rsuite/icons/PageNext'
import FileDownloadIcon from '@rsuite/icons/FileDownload'
import ReloadIcon from '@rsuite/icons/Reload'
import WarningRoundIcon from '@rsuite/icons/WarningRound'
import PageIcon from '@rsuite/icons/Page'
import { Button, Modal } from 'shared'
import useAuth from 'hooks/Auth'
import { notifyError, notifySuccess } from 'utils'
import { ROUTES } from 'routers/Routes'
import EditIcon from '@rsuite/icons/Edit'
import {
  useGetClientNameQuery,
  useGetDocumentFileMutation,
  useGetDocumentHistoryQuery,
  useGetDocumentQuery,
  useReprocessDocumentMutation,
  useCorrectDocumentMutation
} from './documentsApiSlice'
import {
  displayFileName,
  documentTitle,
  errorMessage,
  formatBytes,
  formatDateTime,
  isBusy,
  parseSender
} from './utils'
import { DocumentNavState, documentPath, downloadBlob } from './documentNav'
import StatusBadge from './components/StatusBadge'
import ScanPreview from './components/ScanPreview'
import ExtractedDataPanel from './components/ExtractedDataPanel'
import RemittanceCard from './components/RemittanceCard'
import ActivityTimeline from './components/ActivityTimeline'
import PageHeader from './components/PageHeader'
import EmptyState from './components/EmptyState'
import CorrectionForm from './components/CorrectionForm'
import { CorrectionValues } from './types'
import './documents.scss'

const LIVE_POLL_MS = 5000

const currentUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null')
    return (user?.displayName as string) || (user?.email as string) || undefined
  } catch {
    return undefined
  }
}

const DOCUMENT_TYPE_LABEL: Record<string, string> = {
  PAYMENT_REMITTANCE: 'Payment remittance',
  INVOICE: 'Invoice'
}

const DocumentDetail: FC = () => {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const navState = useLocation().state as DocumentNavState
  const { selectedClientId } = useAuth()

  const detail = useGetDocumentQuery({ id }, { skip: !id })
  const history = useGetDocumentHistoryQuery({ id }, { skip: !id })
  const clientName = useGetClientNameQuery(
    { clientId: selectedClientId ?? '' },
    { skip: !selectedClientId }
  ).data

  // currentData is cleared when the id changes, so Prev/Next never shows the
  // previous document's data beside the next one's preview.
  const detailData = detail.currentData
  const historyData = history.currentData
  const doc = detailData?.document
  const busy = isBusy(doc)

  // Live updates while Document AI is working on it.
  const { refetch: refetchDetail } = detail
  const { refetch: refetchHistory } = history
  useEffect(() => {
    if (!busy) return undefined
    const t = setInterval(() => {
      refetchDetail()
      refetchHistory()
    }, LIVE_POLL_MS)
    return () => clearInterval(t)
  }, [busy, refetchDetail, refetchHistory])

  // ── Preview ────────────────────────────────────────────────────────────
  const [getFile] = useGetDocumentFileMutation()
  const [previewUrl, setPreviewUrl] = useState<string>()
  const [previewBlob, setPreviewBlob] = useState<Blob>()
  const [previewFailed, setPreviewFailed] = useState(false)

  useEffect(() => {
    let url: string | undefined
    let cancelled = false
    setPreviewUrl(undefined)
    setPreviewBlob(undefined)
    setPreviewFailed(false)
    if (!id) return undefined
    getFile({ id })
      .unwrap()
      .then((blob) => {
        if (cancelled) return
        const pdf = blob.type
          ? blob
          : new Blob([blob], { type: 'application/pdf' })
        url = URL.createObjectURL(pdf)
        setPreviewBlob(pdf)
        setPreviewUrl(url)
      })
      .catch(() => !cancelled && setPreviewFailed(true))
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [id, getFile])

  // ── Reprocess ──────────────────────────────────────────────────────────
  // ── Correct ────────────────────────────────────────────────────────────
  const [correct, correctState] = useCorrectDocumentMutation()
  const [editing, setEditing] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>()
  useEffect(() => {
    setEditing(false)
    setServerErrors(undefined)
  }, [id])

  const submitCorrection = async (values: CorrectionValues) => {
    setServerErrors(undefined)
    try {
      await correct({ id, values, reviewedBy: currentUserName() }).unwrap()
      setEditing(false)
      notifySuccess('Corrections sent. This page updates when checks finish.')
    } catch (error) {
      const fields = (error as { data?: { fields?: Record<string, string> } })
        ?.data?.fields
      if (fields) setServerErrors(fields)
      notifyError(errorMessage(error, 'Could not send the corrections.'))
    }
    refetchDetail()
    refetchHistory()
  }

  const [reprocess, reprocessState] = useReprocessDocumentMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const runReprocess = async () => {
    setConfirmOpen(false)
    try {
      await reprocess({ id }).unwrap()
      notifySuccess('Processing started. This page updates when it finishes.')
    } catch (error) {
      notifyError(
        errorMessage(error, 'Could not start processing. Please try again.')
      )
    }
    refetchDetail()
    refetchHistory()
  }

  // ── Prev / next through the list this was opened from ──────────────────
  const ids = navState?.ids ?? []
  const index = ids.indexOf(Number(id))
  const prevId = index > 0 ? ids[index - 1] : undefined
  const nextId =
    index >= 0 && index < ids.length - 1 ? ids[index + 1] : undefined
  const go = (target?: number) =>
    target != null &&
    navigate(documentPath(target), { replace: true, state: navState })
  const back = () => navigate(navState?.from ?? ROUTES.DOCUMENTS)

  // Keyboard: [ and ] step through the list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('input, textarea, select, [contenteditable]')) return
      if (e.key === '[' && prevId != null) go(prevId)
      if (e.key === ']' && nextId != null) go(nextId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (detail.isError) {
    return (
      <div className="dx">
        <div className="dx-card">
          <EmptyState
            tone="danger"
            icon={<PageIcon />}
            title="This document couldn’t be loaded"
            description={errorMessage(
              detail.error,
              'It may have been removed, or the server is unavailable.'
            )}
            action={
              <Button appearance="ghost" onClick={back}>
                Back to documents
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  const title = doc ? documentTitle(doc) : 'Loading…'
  const sender = parseSender(doc?.fromEmail)
  const senderLabel = sender.name
    ? `${sender.name} (${sender.email})`
    : (sender.email ?? '—')
  const fileName =
    doc?.fileName || (doc ? `${displayFileName(doc)}.pdf` : 'document.pdf')
  const problem = detailData?.problem
  const extraction = detailData?.extraction
  const remittance = detailData?.remittance

  const meta: [string, React.ReactNode][] = doc
    ? [
        [
          'Document type',
          DOCUMENT_TYPE_LABEL[doc.documentType ?? ''] ?? doc.documentType ?? '—'
        ],
        [
          'Original file',
          <span className="dx-mono" key="f">
            {displayFileName(doc)}
          </span>
        ],
        [
          doc.source === 'UPLOAD' ? 'Added' : 'Received from',
          doc.source === 'UPLOAD' ? 'Uploaded in the portal' : senderLabel
        ],
        ['Received', formatDateTime(doc.receivedAt)],
        [
          'Inbox',
          doc.inboxEmail ? (
            <span className="dx-mono" key="i">
              {doc.inboxEmail}
            </span>
          ) : (
            '—'
          )
        ],
        ['Pages', detailData?.pageCount ?? '—'],
        ['Size', formatBytes(doc.fileSize)],
        ['Subject', doc.source === 'EMAIL' ? doc.subject || '—' : undefined]
      ]
    : []

  const correction = detailData?.correction

  const renderExtraction = () => {
    if (editing && correction) {
      return (
        <CorrectionForm
          initial={{
            fields: correction.fields,
            lineItems: correction.lineItems
          }}
          saving={correctState.isLoading}
          serverErrors={serverErrors}
          onSubmit={submitCorrection}
          onCancel={() => {
            setEditing(false)
            setServerErrors(undefined)
          }}
        />
      )
    }
    if (!detailData && detail.isFetching) {
      return (
        <div className="dx-skeleton" aria-busy>
          {[60, 45, 75, 50, 66].map((w) => (
            <span key={w} style={{ width: `${w}%` }} />
          ))}
        </div>
      )
    }
    if (extraction) return <ExtractedDataPanel extraction={extraction} />
    if (busy) {
      return (
        <EmptyState
          compact
          title="Reading the document…"
          description="Extracted fields appear here as soon as Document AI finishes."
        />
      )
    }
    return (
      <EmptyState
        compact
        title="Nothing extracted yet"
        description={
          doc?.canReprocess
            ? 'Process this document to read its supplier, dates, totals and line items.'
            : 'No extraction result was stored for this document.'
        }
        action={
          doc?.canReprocess ? (
            <Button
              appearance="primary"
              className="dx-btn-primary"
              size="sm"
              onClick={() => setConfirmOpen(true)}
            >
              Process now
            </Button>
          ) : undefined
        }
      />
    )
  }

  const renderPreview = () => {
    if (previewUrl) {
      return (
        <iframe
          className="dx-preview__pdf"
          src={`${previewUrl}#navpanes=0&view=FitH`}
          title={fileName}
        />
      )
    }
    if (previewFailed) {
      return (
        <EmptyState
          tone="danger"
          title="Preview unavailable"
          description="The original file could not be loaded."
        />
      )
    }
    // ScanPreview draws a blank page while the file loads.
    return undefined
  }

  let reprocessLabel = 'Process now'
  if (doc?.queue === 'FAILED') reprocessLabel = 'Retry processing'
  else if (doc?.extracted) reprocessLabel = 'Process again'

  return (
    <div className="dx dx--review">
      <PageHeader
        breadcrumbs={[
          { label: 'Documents', to: navState?.from ?? ROUTES.DOCUMENTS },
          ...(clientName
            ? [{ label: clientName, to: navState?.from ?? ROUTES.DOCUMENTS }]
            : []),
          { label: title }
        ]}
        title={title}
        subtitle={
          doc && (
            <>
              {extraction?.documentLabel ||
                DOCUMENT_TYPE_LABEL[doc.documentType ?? ''] ||
                'Document'}
              {detailData?.pageCount
                ? ` · ${detailData.pageCount} page${detailData.pageCount === 1 ? '' : 's'}`
                : ''}
              {` · received ${formatDateTime(doc.receivedAt)}`}
            </>
          )
        }
      />

      <div className="dx-actionbar">
        <div className="dx-actionbar__group">
          <Button
            appearance="ghost"
            className="dx-btn-outline"
            startIcon={<ArrowLeftLineIcon />}
            onClick={back}
          >
            Back
          </Button>
          {doc?.canReprocess && (
            <Button
              appearance="primary"
              className="dx-btn-primary"
              startIcon={<ReloadIcon />}
              loading={reprocessState.isLoading}
              onClick={() => setConfirmOpen(true)}
            >
              {reprocessLabel}
            </Button>
          )}
          <Button
            appearance="ghost"
            className="dx-btn-outline"
            startIcon={<FileDownloadIcon />}
            disabled={!previewBlob}
            onClick={() => previewBlob && downloadBlob(previewBlob, fileName)}
          >
            Download
          </Button>
        </div>
        {ids.length > 1 && index >= 0 && (
          <div className="dx-actionbar__group">
            <span className="dx-muted dx-small">
              {index + 1} of {ids.length}
            </span>
            <Button
              appearance="ghost"
              className="dx-btn-outline"
              startIcon={<PagePreviousIcon />}
              disabled={prevId == null}
              onClick={() => go(prevId)}
              title="Previous document ( [ )"
            >
              Prev
            </Button>
            <Button
              appearance="primary"
              className="dx-btn-primary"
              endIcon={<PageNextIcon />}
              disabled={nextId == null}
              onClick={() => go(nextId)}
              title="Next document ( ] )"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <div className="dx-review">
        <div className="dx-review__main">
          <section className="dx-card">
            <div className="dx-status-row">
              <span className="dx-overline">Document status</span>
              {doc && <StatusBadge doc={doc} />}
            </div>
            {doc?.stalled && (
              <div className="dx-alert dx-alert--warning" role="status">
                <WarningRoundIcon />
                <div>
                  <p className="dx-alert__title">Not being processed</p>
                  <p className="dx-alert__body">
                    This document has been waiting since{' '}
                    {formatDateTime(doc.receivedAt)}, much longer than
                    processing takes, so nothing is working on it.
                  </p>
                  <p className="dx-alert__hint">
                    Use “{reprocessLabel}” to send it to Document AI now.
                  </p>
                </div>
              </div>
            )}
            {problem && (
              <div
                className={`dx-alert dx-alert--${doc?.queue === 'FAILED' ? 'danger' : 'warning'}`}
                role="status"
              >
                <WarningRoundIcon />
                <div>
                  <p className="dx-alert__title">
                    {doc?.queue === 'FAILED'
                      ? 'Processing failed'
                      : 'Needs review'}
                  </p>
                  <p className="dx-alert__body">{problem}</p>
                  {correction?.allowed ? (
                    <p className="dx-alert__hint">
                      Compare with the preview and{' '}
                      <button
                        type="button"
                        className="dx-inline-link"
                        onClick={() => setEditing(true)}
                      >
                        correct the data
                      </button>
                      , or use “{reprocessLabel}” to run Document AI again.
                    </p>
                  ) : (
                    doc?.canReprocess && (
                      <p className="dx-alert__hint">
                        Check the preview, then use “{reprocessLabel}” to run
                        Document AI again.
                      </p>
                    )
                  )}
                </div>
              </div>
            )}
            <dl className="dx-fields dx-fields--meta">
              {meta
                .filter(([, value]) => value !== undefined)
                .map(([label, value]) => (
                  <div key={label} className="dx-field">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
            </dl>
          </section>

          <section className="dx-card">
            <div className="dx-card__head">
              <h2 className="dx-card__title">
                {editing ? 'Correct extracted data' : 'Extracted data'}
              </h2>
              {!editing && correction?.allowed && (
                <Button
                  appearance="ghost"
                  size="sm"
                  className="dx-btn-outline"
                  startIcon={<EditIcon />}
                  onClick={() => setEditing(true)}
                >
                  Correct data
                </Button>
              )}
            </div>
            {!editing &&
              correction &&
              !correction.allowed &&
              correction.reason &&
              doc?.extracted && (
                <p className="dx-card__lead dx-card__lead--note">
                  {correction.reason}
                </p>
              )}
            {renderExtraction()}
          </section>

          {remittance && (
            <section className="dx-card">
              <h2 className="dx-card__title">Saved remittance</h2>
              <p className="dx-card__lead">
                The cleaned-up data that was saved and sent on to the connected
                system.
              </p>
              <RemittanceCard remittance={remittance} />
            </section>
          )}

          <section className="dx-card">
            <h2 className="dx-card__title">Activity</h2>
            <ActivityTimeline
              entries={historyData ?? []}
              loading={!historyData && history.isFetching}
            />
          </section>
        </div>

        <aside className="dx-review__side">
          <div className="dx-preview">
            <ScanPreview scanning={busy} className="dx-preview__frame">
              {renderPreview()}
            </ScanPreview>
          </div>
        </aside>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size="xs"
        title={`${reprocessLabel}?`}
        dialogClassName="dx-modal"
        body={
          <p className="dx-modal-text">
            Document AI will read this document again in the background. If the
            result passes validation it is saved and sent on to the connected
            system, the same as an emailed document.
          </p>
        }
        footer={
          <div className="dx-modal-footer">
            <Button appearance="subtle" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              className="dx-btn-primary"
              onClick={runReprocess}
            >
              {reprocessLabel}
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default DocumentDetail
